import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Menu, Bell, HelpCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getInitials } from '@/lib/utils';
import { DOCS_URL, PAGE_SECTIONS, PAGE_TITLES } from '@/lib/constants';
import { useUnreadNotificationCount } from '@/hooks/useApi';
import EnvironmentBadge from './EnvironmentBadge';

interface TopNavProps {
  onOpenMobileMenu: () => void;
}

const TopNav: React.FC<TopNavProps> = ({ onOpenMobileMenu }) => {
  const { user } = useAuth();
  const location = useLocation();
  const pageTitle = PAGE_TITLES[location.pathname] ?? '';
  const section = PAGE_SECTIONS[location.pathname];
  const { data: unreadData } = useUnreadNotificationCount();
  const unreadCount = unreadData?.count ?? 0;

  return (
    <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background/85 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/70 sm:px-6">
      <button
        type="button"
        onClick={onOpenMobileMenu}
        className="rounded-lg p-2 text-muted-foreground hover:bg-surface hover:text-foreground lg:hidden"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-2 text-sm">
        {section && <><span className="hidden text-muted-foreground sm:inline">{section}</span><span className="hidden text-muted-foreground/60 sm:inline" aria-hidden="true">/</span></>}
        <span className="truncate font-medium text-foreground">{pageTitle}</span>
      </nav>

      <div className="ml-auto flex items-center gap-3">
        <EnvironmentBadge />
        <NavLink
          to="/notifications"
          className="relative rounded-lg p-2 text-muted-foreground hover:bg-surface hover:text-foreground"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              data-testid="unread-badge"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-[10px] font-medium leading-none text-white"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </NavLink>
        <a
          href={DOCS_URL}
          target="_blank"
          rel="noreferrer"
          className="rounded-lg p-2 text-muted-foreground hover:bg-surface hover:text-foreground"
          aria-label="Help"
        >
          <HelpCircle className="h-5 w-5" />
        </a>
        <NavLink
          to="/settings"
          className="flex h-8 w-8 items-center justify-center rounded-full bg-accent/10 text-xs font-medium text-accent-text"
          aria-label="Account"
        >
          {user ? getInitials(user.name || user.email) : 'U'}
        </NavLink>
      </div>
    </header>
  );
};

export default TopNav;
